import React, { useCallback, useEffect, useState } from 'react';
import {
  Card, Form, Input, Select, Button, Typography, Skeleton, Alert, Tag, Divider, App, Space,
} from 'antd';
import { SaveOutlined, ReloadOutlined } from '@ant-design/icons';
import { adminApi } from '../services/api';

const { Title, Text, Paragraph } = Typography;

interface SettingEntry {
  value: string | null;
  type: string;
  group: string;
  label: string;
}
type Settings = Record<string, SettingEntry>;

const GRUPPI: Record<string, { titolo: string; nota: string }> = {
  app:    { titolo: 'App',              nota: 'Nome, indirizzi e parametri generali.' },
  stripe: { titolo: 'Stripe Pagamenti', nota: 'Chiavi di incasso. In modalità test le carte vere non funzionano.' },
  meta:   { titolo: 'Meta / Facebook',  nota: 'Credenziali per pubblicare e promuovere i contenuti.' },
};
const ORDINE = ['app', 'stripe', 'meta'];

const STRIPE_TEST = ['stripe_mode', 'stripe_test_pk', 'stripe_test_sk', 'stripe_test_webhook_secret'];
const STRIPE_LIVE = ['stripe_live_pk', 'stripe_live_sk', 'stripe_live_webhook_secret'];

const VERSIONI_META = ['v18.0', 'v19.0', 'v20.0', 'v21.0', 'v22.0', 'v23.0', 'v24.0', 'v25.0'];
const OPZIONI: Record<string, string[]> = {
  stripe_mode: ['test', 'live'],
  meta_graph_api_version: VERSIONI_META,
};

function Campo({ chiave, entry, value, onChange }: {
  chiave: string; entry: SettingEntry; value: string; onChange: (v: string) => void;
}) {
  const opzioni = OPZIONI[chiave];
  const isSelect = entry.type === 'select' || !!opzioni;

  return (
    <Form.Item
      label={entry.label}
      style={{ marginBottom: 16 }}
      extra={chiave === 'stripe_mode' ? 'In «test» nessuna carta reale può pagare.' : undefined}
    >
      {isSelect ? (
        <Select
          value={value || undefined}
          onChange={onChange}
          placeholder="Seleziona…"
          options={(opzioni ?? ['test', 'live']).map(o => ({ value: o, label: o }))}
        />
      ) : entry.type === 'secret' ? (
        // Input.Password mostra e nasconde da solo: prima serviva un pulsante
        // con due icone SVG disegnate a mano.
        <Input.Password value={value} onChange={e => onChange(e.target.value)}
                        placeholder={entry.label} autoComplete="off" />
      ) : (
        <Input value={value} onChange={e => onChange(e.target.value)}
               placeholder={entry.label} autoComplete="off" />
      )}
    </Form.Item>
  );
}

export default function SettingsPage() {
  const { message } = App.useApp();
  const [settings, setSettings] = useState<Settings>({});
  const [values, setValues] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState('');
  const [errore, setErrore] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setErrore('');
    try {
      const res = await adminApi.getSettings();
      setSettings(res.data);
      const init: Record<string, string> = {};
      Object.entries(res.data as Settings).forEach(([k, v]) => { init[k] = v.value ?? ''; });
      setValues(init);
    } catch {
      setErrore('Non è stato possibile caricare le impostazioni.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const salva = async (gruppo: string) => {
    setSaving(gruppo);
    const daSalvare = Object.entries(settings)
      .filter(([, v]) => v.group === gruppo)
      .reduce<Record<string, string>>((acc, [k]) => { acc[k] = values[k] ?? ''; return acc; }, {});
    try {
      await adminApi.updateSettings(daSalvare);
      message.success(`${GRUPPI[gruppo]?.titolo || gruppo}: impostazioni salvate`);
    } catch (e: any) {
      message.error(e?.response?.data?.error || 'Salvataggio non riuscito. Riprova.');
    } finally {
      setSaving('');
    }
  };

  const perGruppo = (g: string) => Object.entries(settings).filter(([, v]) => v.group === g);

  const modo = values.stripe_mode;
  const liveVuote = !values.stripe_live_sk || !values.stripe_live_pk;

  if (loading) {
    return (
      <>
        <Skeleton active paragraph={{ rows: 1 }} style={{ maxWidth: 340, marginBottom: 22 }} />
        {[0, 1, 2].map(i => (
          <Card key={i} style={{ marginBottom: 16 }}><Skeleton active paragraph={{ rows: 4 }} /></Card>
        ))}
      </>
    );
  }

  return (
    <>
      <div className="page-top">
        <div>
          <Title level={3} style={{ margin: 0 }}>Impostazioni</Title>
          <Text type="secondary">Chiavi di incasso, credenziali e parametri del servizio</Text>
        </div>
        <Button icon={<ReloadOutlined />} onClick={load}>Ricarica</Button>
      </div>

      {errore && (
        <Alert type="error" showIcon message={errore} style={{ marginBottom: 16 }}
               action={<Button size="small" onClick={load}>Riprova</Button>} />
      )}

      {/* Il rischio più concreto: credere di incassare mentre si è in test */}
      {modo === 'test' && (
        <Alert
          type="warning" showIcon style={{ marginBottom: 16 }}
          message="Stripe è in modalità test"
          description="Nessuna carta reale può pagare. Inserisci le chiavi live e porta la modalità su «live» per incassare davvero."
        />
      )}
      {modo === 'live' && liveVuote && (
        <Alert
          type="error" showIcon style={{ marginBottom: 16 }}
          message="Modalità live ma chiavi live mancanti"
          description="I pagamenti non funzioneranno finché non inserisci sia la chiave pubblica sia quella segreta."
        />
      )}

      {ORDINE.map(gruppo => {
        const voci = perGruppo(gruppo);
        if (!voci.length) return null;
        const g = GRUPPI[gruppo] || { titolo: gruppo, nota: '' };

        return (
          <Card
            key={gruppo}
            style={{ marginBottom: 16 }}
            title={
              <Space>
                {g.titolo}
                {gruppo === 'stripe' && modo && (
                  <Tag color={modo === 'live' ? 'green' : 'orange'}>{modo}</Tag>
                )}
              </Space>
            }
            extra={
              <Button
                type="primary" icon={<SaveOutlined />}
                loading={saving === gruppo}
                onClick={() => salva(gruppo)}
              >
                Salva
              </Button>
            }
          >
            {g.nota && <Paragraph type="secondary" style={{ marginTop: -6 }}>{g.nota}</Paragraph>}

            <Form layout="vertical" style={{ maxWidth: 620 }}>
              {gruppo === 'stripe' ? (
                <>
                  <Divider titlePlacement="start" plain style={{ marginTop: 0 }}>Prova</Divider>
                  {STRIPE_TEST.filter(k => settings[k]).map(k => (
                    <Campo key={k} chiave={k} entry={settings[k]} value={values[k] ?? ''}
                           onChange={v => setValues(s => ({ ...s, [k]: v }))} />
                  ))}
                  <Divider titlePlacement="start" plain>Produzione</Divider>
                  {STRIPE_LIVE.filter(k => settings[k]).map(k => (
                    <Campo key={k} chiave={k} entry={settings[k]} value={values[k] ?? ''}
                           onChange={v => setValues(s => ({ ...s, [k]: v }))} />
                  ))}
                </>
              ) : (
                voci.map(([k, entry]) => (
                  <Campo key={k} chiave={k} entry={entry} value={values[k] ?? ''}
                         onChange={v => setValues(s => ({ ...s, [k]: v }))} />
                ))
              )}
            </Form>
          </Card>
        );
      })}
    </>
  );
}
